import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plug, Loader2, CheckCircle2, XCircle, RefreshCw, Save, Link2, ShieldCheck, ArrowLeft, Power
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../utils/apiClient';

const API = `${API_BASE}/admin/integrations/prodtrack`;

/**
 * Intégrations → ProdTrack (super admin uniquement).
 * La clé d'intégration est envoyée au serveur puis n'est plus jamais affichée.
 */
const IntegrationsProdTrack = () => {
  const { getAuthHeaders, isSuperAdmin } = useAuth();
  const [liste, setListe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState(null); // tenant_id en cours d'édition

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(API, { headers: getAuthHeaders() });
      setListe(r.data);
    } catch (e) {
      toast.error("Impossible de charger les intégrations");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { if (isSuperAdmin()) charger(); }, [charger, isSuperAdmin]);

  if (!isSuperAdmin()) {
    return <div className="p-8 text-center text-gray-500">Accès réservé au Super Admin</div>;
  }

  if (selection) {
    const ligne = liste.find((l) => l.tenant.id === selection);
    return (
      <ConfigEntreprise
        tenant={ligne.tenant}
        integration={ligne.integration}
        headers={getAuthHeaders()}
        onRetour={() => { setSelection(null); charger(); }}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Plug className="w-6 h-6 text-[#0E346E]" />
          Intégrations — ProdTrack
        </h1>
        <p className="text-gray-500 mt-1">
          Relier une entreprise GembaCoach à une entreprise ProdTrack pour préremplir le TRS des tournées.
          Désactivée par défaut ; chaque entreprise s'active séparément.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center h-32 items-center"><Loader2 className="w-8 h-8 animate-spin text-[#0E346E]" /></div>
      ) : (
        <Card>
          <CardContent className="p-0 divide-y">
            {liste.map(({ tenant, integration }) => (
              <div key={tenant.id} className="flex items-center justify-between p-4">
                <div>
                  <p className="font-semibold text-gray-900">
                    {tenant.name} {tenant.is_demo && <Badge variant="outline" className="ml-2">Démo</Badge>}
                  </p>
                  <p className="text-sm text-gray-500">
                    {integration.enabled
                      ? <>Active — reliée à <b>{integration.prodtrack_entreprise?.nom || 'entreprise ProdTrack non vérifiée'}</b></>
                      : 'Non activée'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={integration.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                    {integration.enabled ? 'Active' : 'Inactive'}
                  </Badge>
                  <Button variant="outline" size="sm" onClick={() => setSelection(tenant.id)}
                          data-testid={`configurer-${tenant.id}`}>
                    Configurer
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const ConfigEntreprise = ({ tenant, integration, headers, onRetour }) => {
  const [enabled, setEnabled] = useState(!!integration.enabled);
  const [baseUrl, setBaseUrl] = useState(integration.base_url || 'https://prodtrack.tmbsaas.com');
  const [apiKey, setApiKey] = useState('');
  const [cleConfiguree, setCleConfiguree] = useState(!!integration.api_key_configured);
  const [test, setTest] = useState(integration.last_test || null);
  const [corr, setCorr] = useState(null);
  const [zoneMap, setZoneMap] = useState(integration.zone_mappings || {});
  const [shiftMap, setShiftMap] = useState(integration.shift_mappings || {});
  const [busy, setBusy] = useState('');

  const chargerCorrespondances = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/${tenant.id}/correspondances`, { headers });
      setCorr(r.data);
      setZoneMap(r.data.zone_mappings || {});
      setShiftMap(r.data.shift_mappings || {});
    } catch (e) {
      setCorr(null);
    }
  }, [tenant.id, headers]);

  useEffect(() => { if (cleConfiguree) chargerCorrespondances(); }, [cleConfiguree, enabled, chargerCorrespondances]);

  const enregistrer = async (activer = enabled) => {
    setBusy('save');
    try {
      const body = { enabled: activer, base_url: baseUrl };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const r = await axios.put(`${API}/${tenant.id}`, body, { headers });
      setEnabled(r.data.enabled);
      setCleConfiguree(r.data.api_key_configured);
      setApiKey('');
      toast.success(activer ? 'Intégration activée' : 'Intégration désactivée');
      if (r.data.api_key_configured) chargerCorrespondances();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setBusy('');
    }
  };

  const tester = async () => {
    setBusy('test');
    try {
      const r = await axios.post(`${API}/${tenant.id}/test`, {}, { headers });
      setTest(r.data);
      r.data.ok ? toast.success(`Connecté à ${r.data.entreprise?.nom}`) : toast.error(r.data.error);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test impossible');
    } finally {
      setBusy('');
    }
  };

  const enregistrerCorrespondances = async () => {
    setBusy('map');
    try {
      const zm = Object.fromEntries(Object.entries(zoneMap).map(([k, v]) => [k, v ? Number(v) : null]));
      const sm = Object.fromEntries(Object.entries(shiftMap).map(([k, v]) => [k, v ? Number(v) : null]));
      await axios.put(`${API}/${tenant.id}/correspondances`, { zone_mappings: zm, shift_mappings: sm }, { headers });
      toast.success('Correspondances enregistrées');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setBusy('');
    }
  };

  const lignes = corr?.prodtrack?.lignes || [];
  const quarts = corr?.prodtrack?.quarts || [];
  const select = 'w-full border rounded-md px-2 py-2 text-sm bg-white';

  return (
    <div className="space-y-6" data-testid="integration-config">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onRetour}><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
        <h1 className="text-xl font-bold text-gray-900">ProdTrack — {tenant.name}</h1>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">1. Connexion</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {/* Interrupteur très visible : état + action en un coup d'œil */}
          <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-xl border-2 ${
            enabled ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 bg-slate-50'}`} data-testid="bloc-activation">
            <div className="flex items-center gap-3">
              <span className={`w-4 h-4 rounded-full ${enabled ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
              <div>
                <p className={`text-lg font-bold ${enabled ? 'text-emerald-800' : 'text-slate-700'}`}>
                  Intégration {enabled ? 'ACTIVE' : 'DÉSACTIVÉE'}
                </p>
                <p className="text-sm text-gray-600">
                  {enabled
                    ? 'Les tournées de cette entreprise récupèrent automatiquement les données ProdTrack.'
                    : 'Aucun appel à ProdTrack : saisie TRS 100 % manuelle.'}
                </p>
              </div>
            </div>
            <Button
              size="lg"
              onClick={() => enregistrer(!enabled)}
              disabled={busy === 'save' || (!enabled && !cleConfiguree && !apiKey.trim())}
              className={`min-w-[220px] h-12 text-base font-bold ${enabled ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white`}
              data-testid="bouton-activation"
            >
              {busy === 'save' ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Power className="w-5 h-5 mr-2" />}
              {enabled ? "Désactiver l'intégration" : "Activer l'intégration"}
            </Button>
          </div>
          <div>
            <label className="text-sm font-medium">Adresse ProdTrack</label>
            <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://prodtrack.tmbsaas.com" />
          </div>
          <div>
            <label className="text-sm font-medium">Clé d'intégration ProdTrack</label>
            <Input type="password" autoComplete="off" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                   placeholder={cleConfiguree ? '•••••••• enregistrée (laisser vide pour la conserver)' : 'pti_…'} />
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Conservée uniquement sur le serveur, jamais réaffichée.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => enregistrer(enabled)} disabled={!!busy} className="bg-[#0E346E]">
              {busy === 'save' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
            <Button variant="outline" onClick={tester} disabled={!!busy || !cleConfiguree} data-testid="tester-connexion">
              {busy === 'test' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Vérifier la connexion
            </Button>
          </div>
          {test && (
            <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${test.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
              {test.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {test.ok ? <>Connecté à <b>{test.entreprise?.nom}</b> (ProdTrack #{test.entreprise?.id})</> : test.error}
            </div>
          )}
        </CardContent>
      </Card>

      {cleConfiguree && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Link2 className="w-4 h-4" /> 2. Correspondances</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            {!corr ? (
              <p className="text-sm text-gray-500">Chargement…</p>
            ) : corr.erreur ? (
              <p className="text-sm text-red-600">{corr.erreur}</p>
            ) : (
              <>
                <div>
                  <p className="font-medium mb-2">Zones machine / production → lignes ProdTrack</p>
                  {corr.zones.length === 0 && <p className="text-sm text-gray-500">Aucune zone de type Machine ou Production.</p>}
                  <div className="space-y-2">
                    {corr.zones.map((z) => (
                      <div key={z.id} className="grid grid-cols-2 gap-3 items-center">
                        <span className="text-sm">{z.site_name ? `${z.site_name} › ` : ''}<b>{z.name}</b></span>
                        <select className={select} value={zoneMap[z.id] || ''}
                                onChange={(e) => setZoneMap({ ...zoneMap, [z.id]: e.target.value })}>
                          <option value="">— Non reliée (saisie manuelle) —</option>
                          {lignes.map((l) => <option key={l.id} value={l.id}>{l.nom}{l.actif ? '' : ' (inactive)'}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="font-medium mb-2">Équipes GembaCoach → quarts ProdTrack</p>
                  <div className="space-y-2">
                    {corr.equipes.map((eq) => (
                      <div key={eq} className="grid grid-cols-2 gap-3 items-center">
                        <span className="text-sm"><b>{eq}</b></span>
                        <select className={select} value={shiftMap[eq] || ''}
                                onChange={(e) => setShiftMap({ ...shiftMap, [eq]: e.target.value })}>
                          <option value="">— Non relié —</option>
                          {quarts.map((q) => <option key={q.id} value={q.id}>{q.nom} ({q.heure_debut} → {q.heure_fin})</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
                <Button onClick={enregistrerCorrespondances} disabled={!!busy} className="bg-[#0E346E]">
                  {busy === 'map' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer les correspondances
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default IntegrationsProdTrack;
